import { Phone } from "lucide-react";
import PageHero from "@/components/PageHero";
import Reveal, { Eyebrow } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";
import { useLanguage } from "@/context/LanguageContext";
import { usePageMeta } from "@/hooks/usePageMeta";
import { PRODUCTS, IMAGES, BUSINESS } from "@/data/site";
import { WhatsAppIcon } from "@/components/Icons";

export default function OurTea() {
  const { t } = useLanguage();
  usePageMeta(t.meta.ourTea.title, t.meta.ourTea.desc);
  const o = t.ourTeaPage;

  return (
    <main>
      <PageHero eyebrow={o.eyebrow} title={o.heading} body={o.body} image={IMAGES.strainer} imgAlt={o.imgAlt} />

      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {PRODUCTS.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}

            <Reveal delay={0.1} className="h-full">
              <div
                data-testid="upcoming-blends-card"
                className="flex h-full flex-col justify-center border border-dashed border-gold/30 bg-velvet/30 p-8 text-center"
              >
                <p className="font-serif text-2xl italic text-gold/80">{o.moreTitle}</p>
                <p className="mt-3 text-sm leading-relaxed text-cream/55">{o.moreBody}</p>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <div
              data-testid="how-to-order-note"
              className="mt-14 flex flex-col items-start gap-6 border hairline bg-velvet/50 p-8 md:flex-row md:items-center md:justify-between md:p-10"
            >
              <div className="max-w-2xl">
                <h3 className="font-serif text-2xl text-gold-light">{o.noteTitle}</h3>
                <p className="mt-3 text-sm leading-relaxed text-cream/70">{o.note}</p>
              </div>
              <div className="flex flex-wrap gap-3">
                <a
                  href={BUSINESS.tel}
                  data-testid="our-tea-call-button"
                  className="inline-flex items-center gap-2.5 bg-gold px-6 py-3.5 text-sm font-medium uppercase tracking-[0.15em] text-obsidian transition-all duration-300 hover:bg-gold-light"
                >
                  <Phone className="h-4 w-4" strokeWidth={1.5} />
                  {BUSINESS.phoneDisplay}
                </a>
                <a
                  href={BUSINESS.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="our-tea-whatsapp-button"
                  className="inline-flex items-center gap-2.5 border border-gold/50 px-6 py-3.5 text-sm uppercase tracking-[0.15em] text-gold transition-all duration-300 hover:bg-gold/10"
                >
                  <WhatsAppIcon className="h-4 w-4" />
                  {t.productPage.whatsapp}
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t hairline bg-coal/40 py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>{o.eyebrow}</Eyebrow>
            <h2 className="mt-6 font-serif text-4xl text-cream sm:text-5xl">{o.brewTitle}</h2>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {o.brew.map((b, i) => (
              <Reveal key={b.t} delay={i * 0.1} className="h-full">
                <div
                  data-testid={`brew-step-${i + 1}`}
                  className="h-full border hairline bg-velvet/40 p-7 transition-all duration-500 hover:border-gold/40"
                >
                  <span className="font-serif text-3xl italic text-gold/70">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-5 font-serif text-xl text-cream">{b.t}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-cream/60">{b.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
